import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { useLang } from "@/context/LanguageContext";

const RED = "#C12B32";

const PATHS = [
  { d: "M0,0 L4,2 L11,9 L18,17 L38,37 L45,45 L61,61 L65,60 L85,49 L111,39 L114,39 L114,147 L122,147 L147,151 L168,157 L192,167 L210,177 L225,188 L235,196 L240,200 L240,376 L-239,376 L-239,124 L-234,127 L-222,137 L-207,147 L-191,156 L-170,165 L-145,172 L-126,175 L-113,176 L-113,284 L-84,273 L-67,264 L-61,260 L-59,260 L-52,268 L-37,282 L-22,298 L-9,311 L-2,319 L2,321 L18,304 L36,286 L43,278 L59,262 L60,260 L65,262 L81,271 L97,278 L114,284 L114,176 L85,176 L85,240 L80,238 L70,231 L60,223 L44,207 L32,191 L27,183 L27,181 L25,181 L22,174 L17,166 L17,162 L-14,163 L-26,185 L-38,202 L-46,211 L-54,218 L-61,225 L-77,237 L-82,240 L-84,240 L-84,176 L-68,174 L-40,169 L-20,163 L-17,162 L-18,154 L-27,138 L-37,124 L-46,113 L-54,106 L-64,97 L-83,84 L-84,147 L-113,147 L-113,39 L-103,42 L-85,49 L-64,60 L-60,61 L-48,49 L-40,42 L-25,26 L-9,10 L-2,2 Z", transform: "translate(239,88)" },
  { d: "M0,0 L4,2 L11,9 L18,17 L38,37 L45,45 L61,61 L65,60 L85,49 L111,39 L114,39 L114,147 L93,147 L62,150 L47,153 L24,159 L21,159 L20,162 L18,162 L19,167 L17,166 L17,162 L-14,163 L-26,185 L-38,202 L-46,211 L-54,218 L-61,225 L-77,237 L-82,240 L-84,240 L-84,176 L-68,174 L-40,169 L-20,163 L-17,162 L-18,154 L-27,138 L-37,124 L-46,113 L-54,106 L-64,97 L-83,84 L-84,147 L-113,147 L-113,39 L-103,42 L-85,49 L-64,60 L-60,61 L-48,49 L-40,42 L-25,26 L-9,10 L-2,2 Z", transform: "translate(239,88)" },
  { d: "M0,0 L1,0 L1,64 L-34,69 L-60,76 L-63,76 L-64,79 L-66,79 L-65,84 L-67,83 L-67,79 L-98,80 L-110,102 L-122,119 L-130,128 L-138,135 L-145,142 L-161,154 L-166,157 L-168,157 L-168,93 L-152,91 L-124,86 L-104,80 L-101,79 L-99,75 L-99,78 L-68,77 L-65,71 L-63,71 L-62,66 L-54,52 L-42,36 L-27,20 L-15,10 Z", transform: "translate(323,171)" },
  { d: "M0,0 L4,2 L14,18 L23,29 L35,42 L38,45 L36,49 L20,65 L13,73 L2,84 L-2,82 L-16,68 L-23,60 L-36,47 L-34,43 L-24,33 L-13,19 L-3,4 Z", transform: "translate(239,284)" },
];

const IntroSeal = ({ onDone }) => {
  const { lang } = useLang();
  const doneRef = useRef(false);

  const finish = () => {
    if (doneRef.current) return;
    doneRef.current = true;
    onDone();
  };

  useEffect(() => {
    window.__lenis?.stop();
    document.body.style.overflow = "hidden";
    const stampSound = new Audio("/audio/sceau.mp3");
    stampSound.volume = 0.5;
    const soundTimer = setTimeout(() => {
      stampSound.play().catch(() => {});
    }, 1500);
    const timer = setTimeout(finish, 3100);
    return () => {
      clearTimeout(timer);
      clearTimeout(soundTimer);
      stampSound.pause();
      document.body.style.overflow = "";
      window.__lenis?.start();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <motion.div
      data-testid="intro-seal"
      onClick={finish}
      exit={{ y: "-100%" }}
      transition={{ duration: 0.9, ease: [0.76, 0, 0.24, 1] }}
      className="grain fixed inset-0 z-[60] flex cursor-pointer flex-col items-center justify-center bg-ink"
      role="presentation"
      aria-label="Introduction — Maison pour l'Art et la Science"
    >
      <div className="relative flex items-center justify-center">
        <motion.span
          aria-hidden="true"
          className="absolute h-56 w-56 rounded-full border border-gold sm:h-72 sm:w-72"
          initial={{ scale: 0.5, opacity: 0 }}
          animate={{ scale: [0.5, 0.5, 1.7], opacity: [0, 0, 0.7, 0] }}
          transition={{ duration: 1.1, delay: 1.5, times: [0, 0.1, 1], ease: "easeOut" }}
        />
        <motion.span
          aria-hidden="true"
          className="absolute h-40 w-40 rounded-full bg-gold/15 blur-2xl sm:h-52 sm:w-52"
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, 0, 0.9, 0.35] }}
          transition={{ duration: 1.4, delay: 1.45, times: [0, 0.1, 0.35, 1] }}
        />
        <motion.div
          data-testid="intro-seal-logo"
          animate={{ scale: [1, 1, 1.14, 1] }}
          transition={{ duration: 2.6, times: [0, 0.58, 0.66, 0.8], ease: "easeInOut" }}
        >
          <svg viewBox="0 0 484 464" className="h-44 w-44 sm:h-56 sm:w-56" aria-hidden="true">
            {PATHS.map((p, i) => (
              <motion.path
                key={i}
                d={p.d}
                transform={p.transform}
                fill={RED}
                stroke={RED}
                strokeWidth={2.5}
                strokeLinejoin="round"
                initial={{ pathLength: 0, fillOpacity: 0 }}
                animate={{ pathLength: 1, fillOpacity: 1 }}
                transition={{
                  pathLength: { delay: 0.25 + i * 0.28, duration: 1.15, ease: [0.65, 0, 0.35, 1] },
                  fillOpacity: { delay: 1.5, duration: 0.45, ease: "easeOut" },
                }}
              />
            ))}
          </svg>
        </motion.div>
      </div>

      <motion.p
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.9, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="mt-10 px-4 text-center font-mono text-[10px] uppercase tracking-[0.4em] text-parchment/80 sm:text-[11px]"
      >
        Maison pour l'Art et la Science
      </motion.p>
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.1, duration: 0.8 }}
        className="mt-3 font-serif text-lg italic text-gold sm:text-xl"
      >
        Chercher · Créer · Transmettre
      </motion.p>

      <motion.p
        data-testid="intro-skip-hint"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.5, duration: 0.6 }}
        className="absolute bottom-8 font-mono text-[9px] uppercase tracking-[0.3em] text-sand/50"
      >
        {lang === "fr" ? "Cliquer pour passer" : "Click to skip"}
      </motion.p>
    </motion.div>
  );
};

export default IntroSeal;
